import { useState, useEffect, useCallback } from 'react';
import {
    getEmployees,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    searchEmployees,
    getEmployeeDashboardStats,
    getContractExpiringEmployees,
    downloadEmployeeCSV,
    calculateWorkingDaysAPI
} from '../services/api';


const useEmployees = () => {
    // **STATE MANAGEMENT**
    const [employees, setEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // **PAGINATION STATE**
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // **FILTER STATE**
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        department: 'all',
        workStatus: 'all',
        contractType: 'all'
    });

    // **DASHBOARD STATE**
    const [dashboardStats, setDashboardStats] = useState(null);
    const [expiringContracts, setExpiringContracts] = useState([]);

    // **LOAD EMPLOYEES**
    const loadEmployees = useCallback(async () => {
        try {
            const params = {
                page: currentPage,
                limit: 20,
                ...filters
            };

            const response = await getEmployees(params);
            if (response.success) {
                console.log('🔥 FRONTEND: loadEmployees response:', response.data.length);
                setEmployees(response.data);
                setFilteredEmployees(response.data);
                setTotalPages(response.pagination.pages);
                console.log('🔥 FRONTEND: State updated with', response.data.length, 'employees');
            }
        } catch (error) {
            setError('Personel listesi yüklenirken hata oluştu');
            console.error('Load employees error:', error);
        }
    }, [currentPage, filters]);

    // **SEARCH EMPLOYEES**
    const handleSearch = useCallback(async () => {
        if (!searchTerm || searchTerm.length < 2) {
            setFilteredEmployees(employees);
            return;
        }

        try {
            const response = await searchEmployees(searchTerm, 50);
            if (response.success) {
                setFilteredEmployees(response.data);
            }
        } catch (error) {
            console.error('Search employees error:', error);
            setError('Arama sırasında hata oluştu');
        }
    }, [searchTerm, employees]);

    // **CREATE EMPLOYEE**
    const createNewEmployee = async (formData) => {
        try {
            setLoading(true);
            const response = await createEmployee(formData);

            if (response.success) {
                setSuccess('Personel başarıyla oluşturuldu');
                await loadEmployees();
                await loadDashboardStats();
                return { success: true, data: response.data };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Personel oluşturulurken hata oluştu';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    // **UPDATE EMPLOYEE**
    const updateExistingEmployee = async (employeeId, formData) => {
        try {
            setLoading(true);
            const response = await updateEmployee(employeeId, formData);

            if (response.success) {
                setSuccess('Personel bilgileri güncellendi');
                await loadEmployees();
                return { success: true };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Personel güncellenirken hata oluştu';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    // **ARSIV EMPLOYEE , database de hala tutuluyor ama listeden cikariliyor**
    const deleteExistingEmployee = async (employeeId) => {
        if (!window.confirm('Bu personeli arşivlemek istediğinizden emin misiniz?')) {
            return { success: false, cancelled: true };
        }

        try {
            setLoading(true);
            const response = await deleteEmployee(employeeId);

            if (response.success) {
                setSuccess('Personel arşivlendi');
                await loadEmployees();
                await loadDashboardStats();
                return { success: true };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Personel silinirken hata oluştu';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    // **LOAD DASHBOARD STATS**
    const loadDashboardStats = useCallback(async () => {
        try {
            const response = await getEmployeeDashboardStats();
            if (response.success) {
                setDashboardStats(response.data);
            }
        } catch (error) {
            console.error('Load dashboard stats error:', error);
        }
    }, []);

    // **LOAD EXPIRING CONTRACTS**
    const loadExpiringContracts = useCallback(async () => {
        try {
            const response = await getContractExpiringEmployees(30);
            if (response.success) {
                setExpiringContracts(response.data);
            }
        } catch (error) {
            console.error('Load expiring contracts error:', error);
        }
    }, []);

    // **HANDLE FILTER CHANGE**
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
        setCurrentPage(1);
    };

    // **HANDLE PAGE CHANGE**
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // **EXPORT CSV**
    const exportToCSV = () => {
        try {
            downloadEmployeeCSV(filteredEmployees, 'personel-listesi');
            setSuccess('Personel listesi indirildi');
        } catch (error) {
            setError('Export sırasında hata oluştu');
        }
    };

    // **WORKING DAYS CALCULATION**
    const calculateWorkingDaysForLeave = useCallback(async (startDate, endDate) => {
        try {
            const response = await calculateWorkingDaysAPI(startDate, endDate);
            if (response.success) {
                return response.data;
            }
        } catch (error) {
            console.error('Working days calculation error:', error);
            setError('Is gunu hesaplanirken hata olustu');
            throw error;
        }
    }, []);

    // **CLEAR MESSAGES**
    const clearMessages = () => {
        setError('');
        setSuccess('');
    };

    // **LOAD INITIAL DATA**
    const loadInitialData = useCallback(async () => {
        try {
            setLoading(true);
            await Promise.all([
                loadEmployees(),
                loadDashboardStats(),
                loadExpiringContracts()
            ]);
        } catch (error) {
            setError('Veriler yüklenirken hata oluştu');
            console.error('Load initial data error:', error);
        } finally {
            setLoading(false);
        }
    }, [loadEmployees, loadDashboardStats, loadExpiringContracts]);

    // **EFFECTS**
    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        loadEmployees();
    }, [currentPage, filters]);

    useEffect(() => {
        if (searchTerm && searchTerm.length >= 2) {
            handleSearch(); // ✅ Sadece 2+ karakter varsa ara
        } else if (searchTerm === '') {
            setFilteredEmployees(employees); // ✅ Boşsa tüm listeyi göster
        }
        // ✅ 1 karakter veya undefined ise hiçbir şey yapma
    }, [searchTerm, employees]);

    // **RETURN HOOK INTERFACE**
    return {
        // State
        employees,
        filteredEmployees,
        loading,
        error,
        success,
        currentPage,
        totalPages,
        searchTerm,
        filters,
        dashboardStats,
        expiringContracts,

        // Actions
        createNewEmployee,
        updateExistingEmployee,
        deleteExistingEmployee,
        handleFilterChange,
        handlePageChange,
        setSearchTerm,
        setSuccess,
        exportToCSV,
        calculateWorkingDaysForLeave,
        clearMessages,
        loadEmployees,
        loadDashboardStats
    };
};

export default useEmployees;